import React, { useState, useRef, useEffect } from 'react';
import Flatpickr from 'react-flatpickr';
import client from '../../../shared/api/client';
import { CAMPAIGN_TYPE_LABELS as campaignTypeLabels } from '../../shared/givingHelpers';
import maskAmount from '../../shared/utils/maskAmount';
import DonationPreview from './DonationPreview';
import FundDropdown from './FundDropdown';
import DonationSettingsSection from './DonationSettingsSection';
import DedicationSection from './DedicationSection';
import GiftQuestionsSection, { buildGiftQuestionFormEntries } from './GiftQuestionsSection';
import '../styles/givingCampaignForm.css';

let nextAmountId = 1;
const makeAmountRow = (amount = '', description = '') => ({
  id: nextAmountId++,
  amount,
  description,
});

const GivingCampaignForm = ({ giving_campaign, funds, venue_ownerships, team, campaign_types, campaign_format, gift_fields = [] }) => {
  const isEdit = giving_campaign && giving_campaign.id;
  const campaignFormat = campaign_format || giving_campaign.format || 'page';
  const isForm = campaignFormat === 'form';
  const venueRequired = !isEdit || giving_campaign.venue_required_for_edit !== false;

  const [name, setName] = useState(giving_campaign.name || '');
  const [campaignCode, setCampaignCode] = useState(giving_campaign.campaign_code || '');
  const [campaignType, setCampaignType] = useState(giving_campaign.campaign_type || '');
  const [goalAmountDollars, setGoalAmountDollars] = useState(
    giving_campaign.goal_amount_cents ? (giving_campaign.goal_amount_cents / 100).toString() : '0'
  );
  const [description, setDescription] = useState(giving_campaign.description || '');
  const [startDate, setStartDate] = useState(giving_campaign.start_date || (isForm ? new Date().toISOString().split('T')[0] : ''));
  const [endDate, setEndDate] = useState(giving_campaign.end_date || (isForm ? new Date(Date.now() + 365*10*24*60*60*1000).toISOString().split('T')[0] : ''));
  const [fundId, setFundId] = useState(giving_campaign.fund ? giving_campaign.fund.id : '');
  const [fundList, setFundList] = useState(funds || []);
  const [venueOwnershipId, setVenueOwnershipId] = useState(giving_campaign.venue_ownership_id || '');
  const venueOwnershipList = venue_ownerships || [];


  const [headline, setHeadline] = useState(giving_campaign.headline || '');
  const [organizerName, setOrganizerName] = useState(giving_campaign.organizer_name || '');
  const [primaryColor, setPrimaryColor] = useState(giving_campaign.primary_color || '#1982C4');
  const [buttonText, setButtonText] = useState(giving_campaign.button_text || 'Give');
  const [coverImage, setCoverImage] = useState(null);

  const allFrequencies = [
    { value: 'one_time', label: 'One-time' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
  ];

  const initEnabledFreqs = (giving_campaign.enabled_frequencies || 'one_time,monthly').split(',');
  const initDescriptions = (() => {
    if (!giving_campaign.preset_amount_descriptions) return {};
    try {
      const parsed = JSON.parse(giving_campaign.preset_amount_descriptions);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch { return {}; }
  })();
  const [amounts, setAmounts] = useState(() => {
    const raw = (giving_campaign.preset_amounts || '25,50,100,250,500')
      .split(',').map(a => a.trim()).filter(Boolean);
    return raw.map(a => makeAmountRow(a, initDescriptions[a] || ''));
  });
  const [enabledFrequencies, setEnabledFrequencies] = useState(initEnabledFreqs);
  const [showOtherAmount, setShowOtherAmount] = useState(giving_campaign.show_other_amount !== false);

  const defaultDedicationOptions = ['In Honor Of', 'In Memory Of'];
  const [dedicationEnabled, setDedicationEnabled] = useState(giving_campaign.dedication_enabled || false);
  const [dedicationOptions, setDedicationOptions] = useState(() => {
    if (giving_campaign.dedication_options) {
      try {
        const parsed = JSON.parse(giving_campaign.dedication_options);
        return Array.isArray(parsed) ? parsed : defaultDedicationOptions;
      } catch { return defaultDedicationOptions; }
    }
    return defaultDedicationOptions;
  });
  const [previewDedicateGift, setPreviewDedicateGift] = useState(false);
  const [previewDedicationType, setPreviewDedicationType] = useState('');
  const [previewHonoree, setPreviewHonoree] = useState('');

  const savedGiftQuestions = giving_campaign.gift_questions || [];
  const [giftQuestions, setGiftQuestions] = useState(
    savedGiftQuestions.map(({ custom_field_id, required }) => ({ custom_field_id, required }))
  );

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);

  const requiredFieldChecks = [
    { label: 'Name', filled: name.trim().length > 0 },
    { label: 'Campaign Code', filled: campaignCode.trim().length > 0 },
    ...(venueRequired ? [{ label: 'Venue', filled: String(venueOwnershipId || '').length > 0 }] : []),
    ...(isForm ? [] : [
      { label: 'Campaign Type', filled: campaignType.length > 0 },
      { label: 'Goal Amount', filled: parseFloat(goalAmountDollars) > 0 },
      { label: 'Start Date', filled: startDate.length > 0 },
      { label: 'End Date', filled: endDate.length > 0 },
    ]),
  ];
  const missingFields = requiredFieldChecks.filter((f) => !f.filled).map((f) => f.label);
  const canSubmitForm = missingFields.length === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmitForm || submitting) return;

    setSubmitting(true);
    setErrors([]);

    const url = isEdit
      ? `/teams/${team.id}/giving/${giving_campaign.id}`
      : `/teams/${team.id}/giving`;

    const formData = new FormData();
    formData.append('giving_campaign[name]', name);
    formData.append('giving_campaign[campaign_code]', campaignCode);
    formData.append('giving_campaign[format]', campaignFormat);
    if (campaignType) formData.append('giving_campaign[campaign_type]', campaignType);
    const parsedGoal = parseFloat(goalAmountDollars);
    const goalCents = Number.isFinite(parsedGoal) ? Math.round(parsedGoal * 100) : 0;
    formData.append('giving_campaign[goal_amount_cents]', goalCents);
    formData.append('giving_campaign[description]', description);
    if (startDate) formData.append('giving_campaign[start_date]', startDate);
    if (endDate) formData.append('giving_campaign[end_date]', endDate);
    formData.append('giving_campaign[headline]', headline);
    formData.append('giving_campaign[organizer_name]', organizerName);
    formData.append('giving_campaign[primary_color]', primaryColor);
    formData.append('giving_campaign[button_text]', buttonText);
    const allAmounts = [];
    const allDescriptions = {};
    amounts.forEach(r => {
      if (r.amount && !allAmounts.includes(r.amount)) allAmounts.push(r.amount);
      if (r.amount && r.description) allDescriptions[r.amount] = r.description;
    });
    formData.append('giving_campaign[preset_amounts]', allAmounts.join(','));
    formData.append('giving_campaign[preset_amount_descriptions]', JSON.stringify(allDescriptions));
    formData.append('giving_campaign[enabled_frequencies]', enabledFrequencies.join(','));
    formData.append('giving_campaign[show_other_amount]', showOtherAmount);
    formData.append('giving_campaign[dedication_enabled]', dedicationEnabled);
    formData.append('giving_campaign[dedication_options]', JSON.stringify(dedicationOptions));
    if (fundId) formData.append('giving_campaign[fund_id]', fundId);
    if (venueOwnershipId) formData.append('giving_campaign[venue_ownership_id]', venueOwnershipId);
    if (coverImage) formData.append('giving_campaign[cover_image]', coverImage);

    buildGiftQuestionFormEntries(giftQuestions, savedGiftQuestions).forEach(([k, v]) => formData.append(k, v));

    try {
      const request = isEdit
        ? client.patch(url, formData)
        : client.post(url, formData);

      const { data } = await request;
      window.location.href = `/teams/${team.id}/giving/${data.id}`;
    } catch (err) {
      const serverErrors = err.response?.data?.errors;
      setErrors(serverErrors || [err.message || 'Something went wrong. Please try again.']);
      setSubmitting(false);
    }
  };

  const coverImageUrl = coverImage
    ? URL.createObjectURL(coverImage)
    : (isEdit && giving_campaign.cover_image_url ? giving_campaign.cover_image_url : null);

  const totalRaisedCents = (isEdit && giving_campaign.total_raised_cents) || 0;
  const totalRaisedDollars = totalRaisedCents / 100;
  const goalDollars = parseFloat(goalAmountDollars) || 0;
  const progressPct = goalDollars > 0 ? Math.min((totalRaisedDollars / goalDollars) * 100, 100) : 0;

  const freqOrder = ['one_time', 'monthly', 'quarterly', 'yearly'];
  const previewFrequencies = freqOrder.filter(f => enabledFrequencies.includes(f));
  const [previewFreq, setPreviewFreq] = useState(previewFrequencies[0] || 'one_time');
  const [previewAmount, setPreviewAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const freqLabels = { one_time: 'Once', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };
  const freqSuffix = { one_time: '', monthly: '/mo', quarterly: '/qtr', yearly: '/yr' };
  const previewAmountRows = amounts
    .filter(r => parseInt(r.amount, 10))
    .map(r => ({ amount: parseInt(r.amount, 10), description: r.description || '' }));
  const previewAmounts = previewAmountRows.map(r => r.amount);

  const activePreviewAmount = previewAmount === 'other' ? (parseFloat(customAmount) || 0) : previewAmount;
  const previewFreqSuffix = previewAmount && previewFreq !== 'one_time'
    ? `/${freqLabels[previewFreq]?.toLowerCase() || ''}`
    : '';
  const previewButtonLabel = activePreviewAmount > 0
    ? `${buttonText || 'Give'} $${activePreviewAmount.toLocaleString()}${previewFreqSuffix}`
    : buttonText || 'Give';

  const formatPreviewCurrency = (dollars) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(dollars);

  const previewContent = (
    <DonationPreview
      primaryColor={primaryColor}
      coverImageUrl={coverImageUrl}
      goalDollars={goalDollars}
      totalRaisedDollars={totalRaisedDollars}
      progressPct={progressPct}
      name={name}
      description={description}
      organizerName={organizerName}
      previewFrequencies={previewFrequencies}
      previewFreq={previewFreq}
      setPreviewFreq={setPreviewFreq}
      freqLabels={freqLabels}
      previewAmountRows={previewAmountRows}
      showOtherAmount={showOtherAmount}
      previewAmount={previewAmount}
      setPreviewAmount={setPreviewAmount}
      customAmount={customAmount}
      setCustomAmount={setCustomAmount}
      dedicationEnabled={dedicationEnabled}
      dedicationOptions={dedicationOptions}
      previewDedicateGift={previewDedicateGift}
      setPreviewDedicateGift={setPreviewDedicateGift}
      previewDedicationType={previewDedicationType}
      setPreviewDedicationType={setPreviewDedicationType}
      previewHonoree={previewHonoree}
      setPreviewHonoree={setPreviewHonoree}
      buttonText={buttonText}
      previewButtonLabel={previewButtonLabel}
    />
  );

  return (
    <div className="gc-builder">
      <div className="gc-top-bar">
        <a href={`/teams/${team.id}/giving`} className="gc-back-link">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M5 12L12 19M5 12L12 5" />
          </svg>
          Back to Campaigns
        </a>
        <div className="gc-page-header">
          <h2 className="gc-page-title">
            {isEdit ? 'Edit Campaign' : `New ${isForm ? 'Donation Form' : 'Campaign Page'}`}
          </h2>
          <div className="gc-header-actions">
            {missingFields.length > 0 && !submitting && (
              <div id="gc-submit-missing" className="gc-submit-missing">
                Required: {missingFields.join(', ')}
              </div>
            )}
            <button
              type="button"
              className="btn btn-setlist-secondary gc-preview-toggle"
              onClick={() => setMobilePreviewOpen(true)}
            >
              <svg style={{ width: 16, height: 16 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              Preview
            </button>
            <a href={`/teams/${team.id}/giving`} className="btn btn-setlist-secondary">
              Cancel
            </a>
            <button
              type="submit"
              form="gc-form"
              className="btn btn-setlist-primary"
              disabled={!canSubmitForm || submitting}
              aria-describedby={missingFields.length > 0 ? 'gc-submit-missing' : undefined}
            >
              {submitting ? 'Saving...' : isEdit ? 'Update Campaign' : 'Create Campaign'}
            </button>
          </div>
        </div>
      </div>

      <div className="gc-builder-content">
        <div className="gc-builder-main">
          {errors.length > 0 && (
          <div className="gc-alert-error">
            <ul>
              {errors.map((error, i) => (
                <li key={i}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form id="gc-form" onSubmit={handleSubmit}>
          <div className="gc-section">
            <div className="gc-section-row">
              <div className="gc-section-info">
                <h3 className="gc-section-title">Campaign Details</h3>
                <p className="gc-section-desc">Core information about your campaign.</p>
              </div>
              <div className="gc-section-fields">
                <div className="gc-field-row">
                  <div className="gc-field">
                    <label className="gc-label" htmlFor="name">Campaign Name <span className="gc-required">*</span></label>
                    <input type="text" className="gc-input" id="name" value={name} onChange={(e) => setName(e.target.value)} required />
                  </div>
                  <div className="gc-field">
                    <label className="gc-label" htmlFor="campaignCode">Campaign Code <span className="gc-required">*</span></label>
                    <div className="gc-input-wrap">
                      <input type="text" className="gc-input" id="campaignCode" value={campaignCode} onChange={(e) => setCampaignCode(e.target.value)} required />
                      <div className="gc-help-trigger">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
                          <circle cx="12" cy="17" r="0.5" fill="currentColor" stroke="none" />
                        </svg>
                        <div className="gc-tooltip">A unique code used to tie donations to this campaign for accounting purposes.</div>
                      </div>
                    </div>
                  </div>
                </div>

                {!isForm && (
                  <div className="gc-field-row">
                    <div className="gc-field">
                      <label className="gc-label" htmlFor="campaignType">Campaign Type <span className="gc-required">*</span></label>
                      <select className="gc-select" id="campaignType" value={campaignType} onChange={(e) => setCampaignType(e.target.value)} required>
                        <option value="">Select type...</option>
                        {(campaign_types || []).map((type) => (
                          <option key={type} value={type}>{campaignTypeLabels[type] || type}</option>
                        ))}
                      </select>
                    </div>
                    <div className="gc-field">
                      <label className="gc-label" htmlFor="goalAmount">Goal Amount <span className="gc-required">*</span></label>
                      <div className="gc-input-group">
                        <span className="gc-input-addon">$</span>
                        <input type="text" inputMode="decimal" className="gc-input" id="goalAmount" value={goalAmountDollars} onChange={(e) => setGoalAmountDollars(maskAmount(e.target.value))} required />
                      </div>
                    </div>
                  </div>
                )}

                {isForm && (
                  <div className="gc-field">
                    <label className="gc-label" htmlFor="goalAmount">Goal</label>
                    <div className="gc-input-group">
                      <span className="gc-input-addon">$</span>
                      <input type="text" inputMode="decimal" className="gc-input" id="goalAmount" value={goalAmountDollars} onChange={(e) => setGoalAmountDollars(maskAmount(e.target.value))} />
                    </div>
                    <div className="gc-hint">Add a goal and progress bar to your page.</div>
                  </div>
                )}

                {!isForm && (
                  <div className="gc-field-row">
                    <div className="gc-field">
                      <label className="gc-label gc-date-label">
                        <span className="gc-label-text">Start Date <span className="gc-required">*</span></span>
                        <Flatpickr
                          className="gc-input"
                          placeholder="Select date"
                          options={{ dateFormat: 'Y-m-d', altInput: true, altFormat: 'n/j/Y', altInputClass: 'gc-input' }}
                          value={startDate}
                          onChange={(dates, dateStr) => setStartDate(dateStr)}
                        />
                      </label>
                    </div>
                    <div className="gc-field">
                      <label className="gc-label gc-date-label">
                        <span className="gc-label-text">End Date <span className="gc-required">*</span></span>
                        <Flatpickr
                          className="gc-input"
                          placeholder="Select date"
                          options={{ dateFormat: 'Y-m-d', altInput: true, altFormat: 'n/j/Y', altInputClass: 'gc-input' }}
                          value={endDate}
                          onChange={(dates, dateStr) => setEndDate(dateStr)}
                        />
                      </label>
                    </div>
                  </div>
                )}

                <FundDropdown fundId={fundId} setFundId={setFundId} fundList={fundList} setFundList={setFundList} team={team} />

                <div className="gc-field">
                  <label className="gc-label" htmlFor="venueOwnership">Venue {venueRequired && <span className="gc-required">*</span>}</label>
                  <select
                    className="gc-select"
                    id="venueOwnership"
                    value={venueOwnershipId}
                    onChange={(e) => setVenueOwnershipId(e.target.value)}
                    required={venueRequired}
                  >
                    <option value="">Select a venue...</option>
                    {venueOwnershipList.map((vo) => (
                      <option key={vo.id} value={vo.id}>{vo.name}</option>
                    ))}
                  </select>
                  <div className="gc-hint">Donations are paid out to this venue.</div>
                </div>

                <div className="gc-field">
                  <label className="gc-label" htmlFor="description">Description</label>
                  <textarea className="gc-textarea" id="description" value={description} onChange={(e) => setDescription(e.target.value)} />
                  <div className="gc-hint">This text appears on the public campaign page.</div>
                </div>
              </div>
            </div>
          </div>

          <DonationSettingsSection
            allFrequencies={allFrequencies}
            enabledFrequencies={enabledFrequencies}
            setEnabledFrequencies={setEnabledFrequencies}
            amounts={amounts}
            setAmounts={setAmounts}
            showOtherAmount={showOtherAmount}
            setShowOtherAmount={setShowOtherAmount}
          />

          <DedicationSection
            dedicationEnabled={dedicationEnabled}
            setDedicationEnabled={setDedicationEnabled}
            dedicationOptions={dedicationOptions}
            setDedicationOptions={setDedicationOptions}
          />

          <GiftQuestionsSection giftFields={gift_fields} questions={giftQuestions} setQuestions={setGiftQuestions} />

          <div className="gc-section">
            <div className="gc-section-row">
              <div className="gc-section-info">
                <h3 className="gc-section-title">{isForm ? 'Form Branding' : 'Public Page Settings'}</h3>
                <p className="gc-section-desc">Control how the campaign appears to donors.</p>
              </div>
              <div className="gc-section-fields">
                {!isForm && (
                  <div className="gc-field">
                    <label className="gc-label" htmlFor="headline">Headline</label>
                    <input type="text" className="gc-input" id="headline" value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="A short tagline for your campaign" />
                  </div>
                )}

                <div className="gc-field">
                  <label className="gc-label" htmlFor="primaryColor">Theme Color</label>
                  <div className="gc-color-row">
                    <input type="color" className="gc-color-swatch" id="primaryColor" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} />
                    <input type="text" className="gc-input gc-color-text" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} />
                  </div>
                </div>

                <div className="gc-field">
                  <label className="gc-label" htmlFor="buttonText">Button Text</label>
                  <input type="text" className="gc-input" id="buttonText" value={buttonText} onChange={(e) => setButtonText(e.target.value)} placeholder="Give" />
                  <div className="gc-hint">Text shown on the donation button (e.g., Give, Donate, Contribute).</div>
                </div>

                <div className="gc-field">
                  <label className="gc-label" htmlFor="organizerName">Organizer Name</label>
                  <input type="text" className="gc-input" id="organizerName" value={organizerName} onChange={(e) => setOrganizerName(e.target.value)} placeholder="e.g., The Vogue Theatre Foundation" />
                  <div className="gc-hint">Displayed as "Organized by ..." on the public page.</div>
                </div>

                <div className="gc-field">
                  <label className="gc-label">Cover Image</label>
                  <div
                    className={`gc-dropzone${dragActive ? ' gc-dropzone-active' : ''}`}
                    onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                    onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => {
                      e.preventDefault(); e.stopPropagation(); setDragActive(false);
                      if (e.dataTransfer.files && e.dataTransfer.files[0]) setCoverImage(e.dataTransfer.files[0]);
                    }}
                    onClick={() => document.getElementById('coverImage').click()}
                  >
                    <input type="file" id="coverImage" accept="image/*" style={{ display: 'none' }} onChange={(e) => setCoverImage(e.target.files[0])} />
                    <div className="gc-dropzone-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V15M17 8L12 3M12 3L7 8M12 3V15" />
                      </svg>
                    </div>
                    <div className="gc-dropzone-text">
                      <span className="gc-dropzone-link">Click to upload</span>
                      <span className="gc-dropzone-secondary"> or drag and drop</span>
                    </div>
                    <div className="gc-dropzone-hint">PNG, JPG or WebP (recommended 2160×1080px)</div>
                  </div>
                  {coverImageUrl && (
                    <div className="gc-cover-thumb">
                      <img src={coverImageUrl} alt="Cover" />
                      <span className="gc-cover-thumb-name">{coverImage ? coverImage.name : 'Current cover image'}</span>
                      {coverImage && (
                        <button type="button" className="gc-cover-thumb-remove" onClick={(e) => { e.stopPropagation(); setCoverImage(null); }}>Remove</button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

        </form>
      </div>

      <div className="gc-builder-sidebar">
        <div className="gc-preview-panel">
          <div className="gc-preview-header">
            <span className="gc-preview-label">Preview</span>
            <span className="gc-preview-badge">Live</span>
          </div>
          {previewContent}
        </div>
      </div>
      </div>

      {mobilePreviewOpen && (
        <div className="gc-preview-modal-overlay" onClick={() => setMobilePreviewOpen(false)}>
          <div className="gc-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gc-preview-modal-header">
              <h3>Preview</h3>
              <button className="gc-preview-modal-close" onClick={() => setMobilePreviewOpen(false)}>&times;</button>
            </div>
            <div className="gc-preview-modal-body">
              {previewContent}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GivingCampaignForm;

